import { useEffect, useState } from 'react';
import { useKlantenStore, type ContactInfo, type DeleteContactReferences } from '../../stores/klanten-store';

interface Props {
  isOpen: boolean;
  contact: ContactInfo | null;
  accountName: string;
  onClose: () => void;
  onSuccess: () => void;
}

/**
 * Bevestigingsdialog voor Contact-verwijderen. Eerst klant-confirm, dan API-call.
 * Bij `CONTACT_IN_USE` toont hij welke records nog naar dit Contact verwijzen
 * zodat de gebruiker eerst die kan opruimen.
 */
export function DeleteContactDialog({ isOpen, contact, accountName, onClose, onSuccess }: Props) {
  const deleteContact = useKlantenStore((s) => s.deleteContact);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [references, setReferences] = useState<DeleteContactReferences | null>(null);

  useEffect(() => {
    if (!isOpen) {
      setSubmitting(false);
      setError(null);
      setReferences(null);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !submitting) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, submitting, onClose]);

  if (!isOpen || !contact) return null;

  async function handleConfirm() {
    if (!contact) return;
    setSubmitting(true);
    setError(null);
    setReferences(null);

    const result = await deleteContact(contact.id);
    setSubmitting(false);

    if (!result.ok) {
      setError(result.error || 'Verwijderen mislukt');
      if (result.code === 'CONTACT_IN_USE' && result.references) {
        setReferences(result.references);
      }
      return;
    }
    onSuccess();
  }

  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40"
      onClick={() => { if (!submitting) onClose(); }}
    >
      <div
        className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 py-4 border-b border-slate-200">
          <h2 className="text-base font-semibold text-slate-900">Contactpersoon verwijderen</h2>
        </div>
        <div className="px-6 py-4 space-y-3 text-sm text-slate-700">
          <p>
            Weet je zeker dat je <span className="font-medium">{contact.name}</span> wilt verwijderen
            bij <span className="font-medium">{accountName}</span>?
          </p>
          <p className="text-xs text-slate-500">
            Deze actie verwijdert de contactpersoon definitief. Records die naar dit contact verwijzen
            (orders, facturen, ritten) blokkeren de verwijdering.
          </p>

          {error && (
            <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
              {error}
              {references && (
                <ul className="mt-2 list-disc list-inside text-xs space-y-0.5">
                  {references.werkOrders > 0 && (
                    <li>{references.werkOrders} werk-order{references.werkOrders !== 1 ? 's' : ''}</li>
                  )}
                  {references.invoices > 0 && (
                    <li>{references.invoices} factu{references.invoices !== 1 ? 'ren' : 'ur'}</li>
                  )}
                  {references.geplandeRitten > 0 && (
                    <li>{references.geplandeRitten} geplande rit{references.geplandeRitten !== 1 ? 'ten' : ''}</li>
                  )}
                  {references.leads > 0 && (
                    <li>{references.leads} lead{references.leads !== 1 ? 's' : ''}</li>
                  )}
                  {references.opportunities > 0 && (
                    <li>{references.opportunities} opportunit{references.opportunities !== 1 ? 'ies' : 'y'}</li>
                  )}
                </ul>
              )}
            </div>
          )}
        </div>
        <div className="px-6 py-3 border-t border-slate-200 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="px-4 py-1.5 text-sm text-slate-600 hover:text-slate-900 disabled:opacity-50"
          >
            Annuleren
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={submitting}
            className="px-4 py-1.5 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 transition-colors disabled:bg-slate-300 disabled:cursor-not-allowed"
          >
            {submitting ? 'Verwijderen…' : 'Verwijderen'}
          </button>
        </div>
      </div>
    </div>
  );
}
